// frontend/src/components/Product/ProductComments.jsx - CLEANED
import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "../../context/AuthContext";
import {
  getComments,
  addComment,
  markCommentHelpful,
  deleteComment,
} from "../../api";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUserCircle,
  faThumbsUp,
  faTrash,
  faReply,
  faSpinner,
  faTractor,
  faStore,
  faClock,
  faStar,
} from "@fortawesome/free-solid-svg-icons";
import Loader from "../UI/Loader";

const ProductComments = ({ productId }) => {
  const { user } = useAuth();
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [commentText, setCommentText] = useState("");
  const [replyText, setReplyText] = useState({});
  const [replyingTo, setReplyingTo] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    fetchComments();
  }, [productId]);

  const fetchComments = async () => {
    try {
      const { data } = await getComments(productId);
      setComments(data.comments);
    } catch (error) {
      console.error("Error fetching comments:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitComment = async (e, parentId = null) => {
    e.preventDefault();
    const text = parentId ? replyText[parentId] : commentText;

    if (!text?.trim()) return;
    if (!user) {
      alert("Please login to comment");
      return;
    }

    setSubmitting(true);
    try {
      await addComment(productId, text, parentId);
      setCommentText("");
      if (parentId) {
        setReplyText({ ...replyText, [parentId]: "" });
        setReplyingTo(null);
      }
      fetchComments();
    } catch (error) {
      console.error("Error adding comment:", error);
      alert(error.response?.data?.message || "Failed to add comment");
    } finally {
      setSubmitting(false);
    }
  };

  const handleHelpful = async (commentId) => {
    if (!user) {
      alert("Please login to mark comments as helpful");
      return;
    }
    try {
      await markCommentHelpful(commentId);
      fetchComments();
    } catch (error) {
      console.error("Error marking helpful:", error);
    }
  };

  const handleDelete = async (commentId) => {
    if (window.confirm("Are you sure you want to delete this comment?")) {
      try {
        await deleteComment(commentId);
        fetchComments();
      } catch (error) {
        console.error("Error deleting comment:", error);
        alert(error.response?.data?.message || "Failed to delete comment");
      }
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getUserIcon = (userType) =>
    userType === "farmer" ? faTractor : faStore;
  const getUserTypeLabel = (userType) =>
    userType === "farmer" ? "Farmer" : "Customer";

  const displayedComments = showAll ? comments : comments.slice(0, 3);

  if (loading) return <Loader size="small" />;

  return (
    <motion.div
      className="product-comments"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
    >
      <div className="comments-header">
        <h3>
          <FontAwesomeIcon icon={faStar} /> Customer Reviews & Comments
          <span className="comment-count">({comments.length})</span>
        </h3>
      </div>

      {user && (
        <motion.form
          onSubmit={handleSubmitComment}
          className="comment-form"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
        >
          <div className="comment-input-wrapper">
            <div className="comment-avatar">
              {user.avatar ? (
                <img src={user.avatar} alt={user.name} />
              ) : (
                <FontAwesomeIcon icon={faUserCircle} />
              )}
            </div>
            <textarea
              placeholder="Share your experience with this product..."
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              rows="3"
              className="comment-textarea"
            />
          </div>
          <div className="comment-form-actions">
            <span className="comment-user-type">
              {getUserTypeLabel(user.userType)}
            </span>
            <button
              type="submit"
              className="submit-btn"
              disabled={submitting || !commentText.trim()}
            >
              {submitting ? (
                <FontAwesomeIcon icon={faSpinner} spin />
              ) : (
                "Post Comment"
              )}
            </button>
          </div>
        </motion.form>
      )}

      <div className="comments-list">
        {comments.length === 0 ? (
          <div className="no-comments">
            <div className="no-comments-icon">💬</div>
            <p>Be the first to comment on this product!</p>
          </div>
        ) : (
          displayedComments.map((comment, index) => (
            <motion.div
              key={comment._id}
              className="comment-item"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <div className="comment-header">
                <div className="comment-user">
                  <FontAwesomeIcon
                    icon={getUserIcon(comment.userType)}
                    className="comment-user-icon"
                  />
                  <div className="comment-user-info">
                    <span className="comment-user-name">
                      {comment.userName}
                    </span>
                    <span className="comment-user-type">
                      {getUserTypeLabel(comment.userType)}
                    </span>
                    <span className="comment-date">
                      <FontAwesomeIcon icon={faClock} />{" "}
                      {formatDate(comment.createdAt)}
                    </span>
                  </div>
                </div>
                {(user?._id === comment.user || user?.role === "admin") && (
                  <button
                    className="delete-comment"
                    onClick={() => handleDelete(comment._id)}
                    aria-label="Delete comment"
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </button>
                )}
              </div>

              <div className="comment-content">{comment.comment}</div>

              <div className="comment-actions">
                <button
                  className="helpful-btn"
                  onClick={() => handleHelpful(comment._id)}
                >
                  <FontAwesomeIcon icon={faThumbsUp} /> Helpful (
                  {comment.helpfulCount})
                </button>
                <button
                  className="reply-btn"
                  onClick={() =>
                    setReplyingTo(
                      replyingTo === comment._id ? null : comment._id,
                    )
                  }
                >
                  <FontAwesomeIcon icon={faReply} /> Reply
                </button>
              </div>

              <AnimatePresence>
                {replyingTo === comment._id && (
                  <motion.form
                    onSubmit={(e) => handleSubmitComment(e, comment._id)}
                    className="reply-form"
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                  >
                    <textarea
                      placeholder="Write a reply..."
                      value={replyText[comment._id] || ""}
                      onChange={(e) =>
                        setReplyText({
                          ...replyText,
                          [comment._id]: e.target.value,
                        })
                      }
                      rows="2"
                    />
                    <div className="reply-actions">
                      <button
                        type="submit"
                        className="reply-submit"
                        disabled={submitting || !replyText[comment._id]?.trim()}
                      >
                        {submitting ? (
                          <FontAwesomeIcon icon={faSpinner} spin />
                        ) : (
                          "Post Reply"
                        )}
                      </button>
                      <button
                        type="button"
                        className="reply-cancel"
                        onClick={() => setReplyingTo(null)}
                      >
                        Cancel
                      </button>
                    </div>
                  </motion.form>
                )}
              </AnimatePresence>

              {comment.replies?.length > 0 && (
                <div className="replies-list">
                  {comment.replies.map((reply) => (
                    <div key={reply._id} className="reply-item">
                      <div className="reply-header">
                        <div className="reply-user">
                          <FontAwesomeIcon
                            icon={getUserIcon(reply.userType)}
                            className="reply-user-icon"
                          />
                          <div className="reply-user-info">
                            <span className="reply-user-name">
                              {reply.userName}
                            </span>
                            <span className="reply-user-type">
                              {getUserTypeLabel(reply.userType)}
                            </span>
                            <span className="reply-date">
                              <FontAwesomeIcon icon={faClock} />{" "}
                              {formatDate(reply.createdAt)}
                            </span>
                          </div>
                        </div>
                        {(user?._id === reply.user ||
                          user?.role === "admin") && (
                          <button
                            className="delete-comment"
                            onClick={() => handleDelete(reply._id)}
                            aria-label="Delete reply"
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </button>
                        )}
                      </div>
                      <div className="reply-content">{reply.comment}</div>
                      <div className="reply-actions">
                        <button
                          className="helpful-btn"
                          onClick={() => handleHelpful(reply._id)}
                        >
                          <FontAwesomeIcon icon={faThumbsUp} /> Helpful (
                          {reply.helpfulCount})
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </motion.div>
          ))
        )}
      </div>

      {comments.length > 3 && (
        <div className="show-more">
          <button onClick={() => setShowAll(!showAll)}>
            {showAll ? "Show Less" : `Show All ${comments.length} Comments`}
          </button>
        </div>
      )}
    </motion.div>
  );
};

export default ProductComments;
